import {
	DatePicker,
	FormFieldControl,
	Input,
	Select,
	SlideoutMenu,
	StatusModal,
	Tabs,
	ToastCustom,
} from "@components";
import { Button } from "@components/button/button-untitledui";
import { FileUpload } from "@components/file-upload";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { CheckCircle, CursorClick02, List, Signal01 } from "@untitledui/icons";
import { maskOdometer } from "@utils";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { OdometerTimePicker } from "@/features/vehicle/components/odometer-time-picker";
import {
	createOdometerManualUpdateSchema,
	type OdometerManualUpdateSchema,
} from "@/features/vehicle/schemas";
import { vehiclesService } from "@/features/vehicle/services";
import { VEHICLE_ODOMETER_ADJUSTMENT_REASON } from "@/features/vehicle/types";
import type { OdometerHistoryOrigin } from "@/features/vehicle/types/odometer-ui.types";
import { cx } from "@/utils/cx";
import { mapOdometerApiSourceToHistoryOrigin } from "../handlers";
import {
	useCreateVehicleOdometerManual,
	useGetVehicleOdometerSummary,
} from "../query";

dayjs.extend(customParseFormat);

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const ODOMETER_EVIDENCE_ACCEPT =
	".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/jpg,image/webp";

const REASON_OPTIONS = [
	{
		id: "Telemetria desatualizada",
		label: "Telemetria desatualizada",
		value: VEHICLE_ODOMETER_ADJUSTMENT_REASON.TELEMETRY_OUTDATED,
	},
	{
		id: "Cadastro manual com erro de digitação",
		label: "Cadastro manual com erro de digitação",
		value: VEHICLE_ODOMETER_ADJUSTMENT_REASON.MANUAL_TYPING_ERROR,
	},
	{
		id: "Mudança de fonte de leitura de dados",
		label: "Mudança de fonte de leitura de dados",
		value: VEHICLE_ODOMETER_ADJUSTMENT_REASON.DATA_SOURCE_CHANGED,
	},
	{
		id: "Outros",
		label: "Outros",
		value: VEHICLE_ODOMETER_ADJUSTMENT_REASON.OTHER,
	},
];

const DATE_TIME_READ_TABS = [
	{ id: "current", label: "Usar data/hora atuais", isDisabled: false },
	{
		id: "retroactive",
		label: "Informar data/hora retroativas",
		isDisabled: false,
	},
];

type OdometerManualUpdateDrawerProps = {
	isOpen: boolean;
	vehicleId: string;
	currentOdometer: number;
	vehicleHeaderSubtitle: string;
	onOpenChange: (open: boolean) => void;
	onSubmitSuccess?: () => void;
};

function ReadingSourceDisplay({
	origin,
}: {
	origin: OdometerHistoryOrigin | null;
}) {
	if (!origin) {
		return <span className="font-semibold text-fg-secondary text-sm">—</span>;
	}
	const iconByOrigin: Record<OdometerHistoryOrigin, ReactNode> = {
		TELEMETRIA: <Signal01 className="size-4" />,
		CHECKLIST: <List className="size-4" />,
		MANUAL: <CursorClick02 className="size-4" />,
	};
	return (
		<div className="flex items-center gap-2 font-semibold text-sm">
			{iconByOrigin[origin]}
			{origin}
		</div>
	);
}

const defaultValues: OdometerManualUpdateSchema = {
	newValue: "",
	useCurrentDateTime: true,
	customDate: null,
	customTime: "",
	reason: null,
	evidenceName: "",
	evidenceFile: null,
};

function parseOdometerValue(value: string): number {
	const onlyDigits = value.replace(/\D/g, "");
	if (onlyDigits.length === 0) {
		return 0;
	}
	return Number(onlyDigits);
}

function buildReadingDateTime(values: OdometerManualUpdateSchema): string {
	if (values.useCurrentDateTime) {
		return dayjs().toISOString();
	}
	if (values.customDate === null) {
		return dayjs().toISOString();
	}
	return dayjs(
		`${values.customDate.year}-${values.customDate.month}-${values.customDate.day} ${values.customTime.trim()}`,
		"YYYY-M-D HH:mm"
	).toISOString();
}

export function OdometerManualUpdateDrawer({
	isOpen,
	vehicleId,
	currentOdometer,
	vehicleHeaderSubtitle,
	onOpenChange,
	onSubmitSuccess,
}: OdometerManualUpdateDrawerProps) {
	const [isOdometerFocused, setIsOdometerFocused] = useState(false);
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
	const schema = useMemo(
		() => createOdometerManualUpdateSchema(currentOdometer),
		[currentOdometer]
	);
	const { mutateAsync: createManualOdometer } =
		useCreateVehicleOdometerManual();
	const { data: odometerSummary } = useGetVehicleOdometerSummary(vehicleId);

	const lastReadingSourceOrigin = useMemo(() => {
		const source = odometerSummary?.lastOdometerSource;
		if (source === null || source === undefined) {
			return null;
		}
		return mapOdometerApiSourceToHistoryOrigin(source);
	}, [odometerSummary?.lastOdometerSource]);

	const getOdometerDisplayValue = (value: string): string => {
		const formattedValue = maskOdometer(value);
		const odometerWithKmSuffix = formattedValue ? `${formattedValue} km` : "";
		return isOdometerFocused ? formattedValue : odometerWithKmSuffix;
	};

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		watch,
		clearErrors,
		formState: { errors, isSubmitting },
	} = useForm<OdometerManualUpdateSchema>({
		resolver: zodResolver(schema),
		defaultValues,
		mode: "onChange",
	});

	const useCurrentDateTime = watch("useCurrentDateTime");
	const newValue = watch("newValue");
	const selectedDateTimeReadTab = useCurrentDateTime
		? "current"
		: "retroactive";
	const evidenceFile = watch("evidenceFile");
	const hasManualRegression =
		newValue.trim() !== "" && parseOdometerValue(newValue) < currentOdometer;

	useEffect(() => {
		if (!hasManualRegression) {
			clearErrors(["reason", "evidenceFile"]);
		}
	}, [hasManualRegression, clearErrors]);

	const handleClose = () => {
		reset(defaultValues);
		onOpenChange(false);
	};

	const onSubmit = handleSubmit(async (values) => {
		try {
			const newOdometer = parseOdometerValue(values.newValue);
			const hasOdometerRegression = newOdometer < currentOdometer;
			const selectedReason = REASON_OPTIONS.find(
				(option) => option.id === values.reason
			);

			let attachmentFileKey: string | undefined;
			let attachmentFileName: string | undefined;

			if (values.evidenceFile) {
				const presignedUpload =
					await vehiclesService.getVehicleOdometerAttachmentPresignedUpload(
						vehicleId,
						{
							fileName: values.evidenceFile.name,
						}
					);

				await vehiclesService.uploadToPresignedUrl(
					presignedUpload.uploadUrl,
					values.evidenceFile,
					values.evidenceFile.type
				);

				attachmentFileKey = presignedUpload.fileKey;
				attachmentFileName = presignedUpload.fileName;
			}
			const readingDateTime = buildReadingDateTime(values);

			await createManualOdometer({
				vehicleId,
				payload: {
					newOdometer,
					readingDateTime,
					...(hasOdometerRegression && selectedReason
						? { adjustmentReason: selectedReason.value }
						: {}),
					...(hasOdometerRegression && values.reason
						? { adjustmentReasonDescription: values.reason }
						: {}),
					...(attachmentFileKey ? { attachmentFileKey } : {}),
					...(attachmentFileName ? { attachmentFileName } : {}),
				},
			});

			onSubmitSuccess?.();
			handleClose();
			setIsSuccessModalOpen(true);
		} catch (_error) {
			toast(
				ToastCustom({ title: "Erro ao atualizar hodômetro", variant: "error" })
			);
		}
	});

	const closeSuccessModal = () => {
		setIsSuccessModalOpen(false);
	};

	return (
		<>
			<SlideoutMenu
				className="w-full max-w-[625px]"
				isOpen={isOpen}
				onOpenChange={(open) => {
					if (!open) {
						handleClose();
					}
				}}
			>
				{({ close }) => (
					<>
						<SlideoutMenu.Header onClose={close}>
							<div className="flex flex-col gap-1 pr-8">
								<h4 className="font-semibold text-fg-primary text-lg">
									Atualização Manual de Hodômetro
								</h4>
								<p className="text-fg-secondary text-sm">
									{vehicleHeaderSubtitle}
								</p>
							</div>
						</SlideoutMenu.Header>
						<SlideoutMenu.Content>
							<form className="flex flex-col gap-4" onSubmit={onSubmit}>
								<div className="grid grid-cols-2 overflow-hidden rounded-[4px] border border-[#d5d7da]">
									<div className="bg-[#e9eaeb] p-4">
										<p className="mb-2 font-bold text-fg-primary text-sm">
											Último Registro no Sistema
										</p>
										<p className="font-bold text-fg-primary text-xl">
											{currentOdometer.toLocaleString("pt-BR")}{" "}
											<span className="font-medium text-sm">km</span>
										</p>
									</div>
									<div className="bg-[#e9eaeb] p-4">
										<p className="mb-2 font-bold text-fg-primary text-sm">
											Fonte Utilizada
										</p>
										<ReadingSourceDisplay origin={lastReadingSourceOrigin} />
									</div>
								</div>

								<Controller
									control={control}
									name="newValue"
									render={({ field }) => {
										const displayValue = getOdometerDisplayValue(
											field.value ?? ""
										);
										return (
											<FormFieldControl
												error={errors.newValue?.message}
												htmlFor="new-hodometer"
												isRequired
												label="Novo Hodômetro"
											>
												<Input
													aria-invalid={!!errors.newValue?.message}
													aria-label="Novo valor de hodômetro"
													inputMode="numeric"
													maxLength={7}
													onBlur={() => {
														setIsOdometerFocused(false);
													}}
													onChange={({ target }) => {
														const newValueField = target.value;
														// Remove tudo que não for dígito (pontos serão adicionados pela máscara)
														// Remove também " km" caso o usuário tenha digitado
														const onlyNumbers = newValueField.replace(
															/\D/g,
															""
														);

														// Limita a 6 caracteres
														if (onlyNumbers.length > 6) {
															return;
														}

														field.onChange(onlyNumbers);
													}}
													onFocus={() => {
														setIsOdometerFocused(true);
													}}
													placeholder="45.000"
													value={displayValue}
												/>
											</FormFieldControl>
										);
									}}
								/>

								<div className="flex flex-col gap-2">
									<p className="font-medium text-fg-primary text-sm">
										Data e Hora da Leitura
									</p>
									<Tabs
										className="w-fit"
										onSelectionChange={(tabId) =>
											setValue("useCurrentDateTime", tabId === "current", {
												shouldValidate: true,
											})
										}
										selectedKey={selectedDateTimeReadTab}
									>
										<Tabs.List
											className="w-fit"
											items={DATE_TIME_READ_TABS}
											type="button-minimal"
										>
											{(tab) => <Tabs.Item {...tab} />}
										</Tabs.List>
									</Tabs>
								</div>

								{useCurrentDateTime ? null : (
									<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
										<Controller
											control={control}
											name="customDate"
											render={({ field }) => (
												<FormFieldControl
													error={errors.customDate?.message}
													htmlFor="custom-date"
												>
													<DatePicker
														className="w-full"
														maxValue={today(getLocalTimeZone())}
														onChange={(date) =>
															field.onChange(
																date
																	? new CalendarDate(
																			date.year,
																			date.month,
																			date.day
																		)
																	: null
															)
														}
														value={field.value}
													/>
												</FormFieldControl>
											)}
										/>
										<Controller
											control={control}
											name="customTime"
											render={({ field }) => (
												<FormFieldControl
													error={errors.customTime?.message}
													htmlFor="custom-time"
												>
													<OdometerTimePicker
														onChange={field.onChange}
														value={field.value}
													/>
												</FormFieldControl>
											)}
										/>
									</div>
								)}

								<Controller
									control={control}
									name="reason"
									render={({ field }) => (
										<FormFieldControl
											error={errors.reason?.message}
											htmlFor="reason"
											isRequired={hasManualRegression}
											label="Motivo do ajuste"
										>
											<Select
												aria-label="Motivo do ajuste"
												className="w-full"
												isInvalid={!!errors.reason?.message}
												items={REASON_OPTIONS}
												onChange={(value) => field.onChange(value)}
												placeholder="Selecione o motivo"
												value={field.value?.toString() ?? ""}
											>
												{(item) => (
													<Select.Item id={item.id}>{item.label}</Select.Item>
												)}
											</Select>
										</FormFieldControl>
									)}
								/>

								<Controller
									control={control}
									name="evidenceFile"
									render={({ field }) => (
										<FormFieldControl
											error={errors.evidenceFile?.message}
											htmlFor="evidence-attachment"
											isRequired={hasManualRegression}
											label="Foto da Evidência (Painel)"
										>
											<FileUpload.Root className="w-full max-w-full">
												<FileUpload.DropZone
													accept={ODOMETER_EVIDENCE_ACCEPT}
													allowsMultiple={false}
													className={cx(
														!!errors.evidenceFile?.message &&
															"ring-border-error_subtle"
													)}
													hint={
														"Envie apenas arquivos PNG, JPG, JPEG e WEBP.\n" +
														`Tamanho máximo: ${MAX_FILE_SIZE_BYTES / 1024 / 1024} MB`
													}
													maxSize={MAX_FILE_SIZE_BYTES}
													onDropFiles={(files) => {
														const file = files.length > 0 ? files[0] : null;
														field.onChange(file);
														setValue("evidenceName", file?.name ?? "", {
															shouldValidate: true,
														});
													}}
													onDropUnacceptedFiles={() =>
														toast(
															<ToastCustom
																description="Arquivo não aceito. Verifique o formato ou tamanho."
																title="Erro"
															/>
														)
													}
													onSizeLimitExceed={() =>
														toast(
															<ToastCustom
																description="Arquivo não aceito. Verifique o formato ou tamanho."
																title="Erro"
															/>
														)
													}
												/>
												{!!evidenceFile?.name && (
													<FileUpload.List>
														<FileUpload.ListItemProgressBar
															{...evidenceFile}
															key={evidenceFile.name}
															name={evidenceFile.name}
															onDelete={() => {
																field.onChange(null);
																setValue("evidenceName", "", {
																	shouldValidate: true,
																});
															}}
															progress={100}
															size={evidenceFile.size}
														/>
													</FileUpload.List>
												)}
											</FileUpload.Root>
										</FormFieldControl>
									)}
								/>
							</form>
						</SlideoutMenu.Content>
						<SlideoutMenu.Footer className="flex gap-2">
							<Button
								aria-label="Cancelar atualização manual"
								className="w-full"
								color="secondary"
								onClick={handleClose}
								size="lg"
							>
								Cancelar
							</Button>
							<Button
								aria-label="Salvar atualização manual"
								className="w-full"
								iconLeading={CheckCircle}
								isDisabled={isSubmitting}
								isLoading={isSubmitting}
								onClick={onSubmit}
								size="lg"
							>
								Salvar Atualização
							</Button>
						</SlideoutMenu.Footer>
					</>
				)}
			</SlideoutMenu>
			<StatusModal
				actions={[
					{ label: "Fechar", onClick: closeSuccessModal, color: "secondary" },
				]}
				description="Clique em fechar para ver o novo valor."
				isOpen={isSuccessModalOpen}
				onClose={closeSuccessModal}
				title="Atualização concluída!"
				variant="success"
			/>
		</>
	);
}
